'use client';

import { useState } from 'react';

import {
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { cn } from '@/lib';

import { DataTableColumnHeader } from '..';
import { DataTableViewOptions } from './column-toggle';
import { DataTablePagination } from './data-table-pagination';

/**
 * @file index.jsx
 * @description Data table component
 * @version 0.0.1
 * Created at: 02/09/2025
 * Updated at: 02/09/2025
 */
export const DataTable = ({ columns, data, noIndexCol = true, className }) => {
  //-------------- State & Variables --------------//
  const [sorting, setSorting] = useState([]);
  const [columnFilters, setColumnFilters] = useState([]);
  const [globalFilter, setGlobalFilter] = useState('');

  // Inject index column automatically
  const enhancedColumns = [
    {
      id: 'index',
      header: ({ column }) => <DataTableColumnHeader column={column} title="SR No." />,
      cell: (info) => info.row.index + 1,
      size: 20,
    },
    ...columns,
  ];

  const table = useReactTable({
    data,
    columns: noIndexCol ? columns : enhancedColumns,
    getPaginationRowModel: getPaginationRowModel(),
    getCoreRowModel: getCoreRowModel(),

    onSortingChange: setSorting,
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),

    onColumnFiltersChange: setColumnFilters,

    // Global filter
    onGlobalFilterChange: setGlobalFilter,
    globalFilterFn: 'includesString',

    state: {
      sorting,
      columnFilters,
      globalFilter,
    },
    initialState: {
      pagination: {
        pageSize: 12,
      },
    },
  });
  //-------------- Use Effects --------------//

  //-------------- Other Methods --------------//

  return (
    <div className={cn('w-full space-y-4', className)}>
      <DataTableViewOptions
        table={table}
        globalFilter={globalFilter}
        setGlobalFilter={setGlobalFilter}
      />
      <div className="w-full overflow-hidden rounded-md border">
        <Table className={'relative'}>
          <TableHeader className={'bg-muted sticky top-0 z-10'}>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => {
                  return (
                    <TableHead key={header.id}>
                      {header.isPlaceholder
                        ? null
                        : flexRender(header.column.columnDef.header, header.getContext())}
                    </TableHead>
                  );
                })}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody className="**:data-[slot=table-cell]:first:w-8">
            {table.getRowModel().rows?.length ? (
              table.getRowModel().rows.map((row) => (
                <TableRow
                  key={row.id}
                  data-state={row.getIsSelected() && 'selected'}
                  className="even:bg-muted/30 odd:bg-background hover:bg-muted/50 transition-colors"
                >
                  {row.getVisibleCells().map((cell) => (
                    <TableCell key={cell.id} className="pl-4!">
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={columns.length} className="h-24 text-center">
                  No results.
                </TableCell>
              </TableRow>
            )}
          </TableBody>

        </Table>
      </div>
      <DataTablePagination table={table} />
    </div>
  );
};
